
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

// Every button answers a press: it shrinks to 97% while held (where motion is
// allowed) and takes its hover colour, so a touch screen, which shows no hover
// at all (`future.hoverOnlyWhenSupported` in tailwind.config.ts), still gets
// feedback. The mobile tap highlight is off for the same reason: the pressed
// state is the feedback, and the browser's grey flash on top of it read as a
// second, clumsier press.
//
// Transitions name their properties and run 150ms on a strong ease-out, so a
// press registers at once (a hover arrives over 200ms and leaves over 150ms,
// see the default variant). The old default variant animated EVERY property
// over 500ms, so Add to Cart felt sluggish to answer. The easing is an
// arbitrary property because tailwindcss-animate also claims the ease prefix,
// and Tailwind silently drops an arbitrary ease class it finds ambiguous.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[color,background-color,border-color,box-shadow,transform] duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] motion-safe:active:scale-[0.97] [-webkit-tap-highlight-color:transparent] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // INK, not violet (owner decisions 2026-10-21, DESIGN.md). It rests a
        // step short of full ink (light grey on the dark page, dark grey on the
        // light one) and on hover or press goes all the way, white or
        // near-black, with the `--action-glow` halo; the tokens and their
        // contrast are in src/app/globals.css. On a seller's page whose seller
        // picked an accent (`[data-seller-accent]` on <body>) the same classes
        // give that accent, solid on hover with a lift shadow in its own colour
        // (clampAccentHex holds the accent at 4.5:1 under its label).
        // `relative overflow-hidden` stay from the old violet variant (it
        // clipped a decorative wash) because call sites lay out against them;
        // overflow does not clip the element's own shadow, so the glow shows.
        default:
          "relative overflow-hidden border border-transparent bg-action text-action-foreground hover:bg-[hsl(var(--action-hover)/var(--action-hover-alpha))] hover:shadow-action-glow hover:duration-200 active:bg-[hsl(var(--action-hover)/var(--action-hover-alpha))] active:shadow-action-glow disabled:bg-muted disabled:border-border disabled:text-muted-foreground disabled:opacity-100",
        // The buy action, one per screen: Add to cart, Checkout, Pay (DESIGN.md,
        // The Brand Jobs Rule). The brand accent with its contrast-picked label
        // (white in light, ink in dark), darker on hover in light and lighter
        // in dark; on a seller's page it is their accent, solid on hover, as
        // their other buttons are. No glow: the colour is the signal.
        brand:
          "relative overflow-hidden border border-transparent bg-brand text-brand-foreground hover:bg-brand-hover active:bg-brand-hover disabled:bg-muted disabled:border-border disabled:text-muted-foreground disabled:opacity-100",
        destructive:
          "border border-destructive bg-background text-destructive hover:bg-destructive hover:text-destructive-foreground active:bg-destructive active:text-destructive-foreground",
        warning:
          "border border-warning bg-background text-warning hover:bg-warning/10 hover:text-warning active:bg-warning/15",
        diamond:
          "border border-diamond bg-background text-diamond hover:bg-diamond/10 hover:text-diamond active:bg-diamond/15",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent active:text-accent-foreground",
        secondary:
          "border border-transparent bg-secondary text-secondary-foreground hover:border-border hover:bg-muted active:bg-muted",
        ghost: "hover:bg-accent hover:text-accent-foreground active:bg-accent active:text-accent-foreground",
        // A link: the `link` colour (ink here, the seller's accent on their
        // page), underlined at rest because colour no longer marks it, the
        // underline firming on hover (DESIGN.md, violet is light, not paint).
        // A text link does not shrink when pressed.
        link: "text-link underline decoration-link/40 underline-offset-4 hover:decoration-link motion-safe:active:scale-100",
      },
      // `coarse:` (see tailwind.config.ts) lifts the small sizes to a 44px
      // finger target on touch devices only. Mouse and trackpad users keep the
      // dense sizes, so nothing about the desktop layout moves. `default` and
      // `lg` are already 40px and 44px and are left alone.
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 coarse:h-11 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10 coarse:h-11 coarse:w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Show a leading spinner and disable the button. Ignored when `asChild` is set. */
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    if (asChild) {
      // `asChild` forwards to a single child via Slot; we can't inject a spinner
      // safely there, so loading is ignored to avoid breaking child structure.
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          disabled={disabled}
          {...props}
        >
          {children}
        </Comp>
      )
    }
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
